// @ts-check
import BaseController from './BaseController';
import { parseHereGeocodeResult, parseSocexResult } from '../helpers/Search';
import { dealAreaFields, getFeatureBoundingBox } from '../helpers/Util';

import ApplicationFrameType from '../enums/ApplicationFrameType';
import LocationAnalysisType from '../enums/LocationAnalysisType';
import LocationAnalysisItemOrigin from '../enums/LocationAnalysisItemOrigin';

import LocationAnalysisItem from '../objects/LocationAnalysisItem';
import InitialView from '../objects/InitialView';

import ProjectDataSource from '../dataSources/ProjectDataSource';
import MetadataDataSource from '../dataSources/MetadataDataSource';
import CustomMapSelectionDataSource from '../dataSources/CustomMapSelectionDataSource';

import { HelpTourVersions } from '../enums/HelpTourDefinitions';
import {SELFSTORAGE_POINT_TYPE} from "../enums/SelfStoragePointType";

class OverrideController extends BaseController {
    static get name() {
        return 'OverrideController';
    }

    static getInstance(options) {
        return new OverrideController(options);
    }

    onActivate() {
        this.projectDataSource = this.activateSource(ProjectDataSource);
        this.metadataDataSource = this.activateSource(MetadataDataSource);
        this.customMapSelectionDataSource = this.activateSource(CustomMapSelectionDataSource);

        /** @type {import('../../../').OverrideProjectOptions} */
        this._overrideProjectOptions = {};

        // Override location and location analysis if set
        // In order to override location analysis, the location must be set as well
        let shouldShowInfoBubblePointFeature = false;
        this.bus.emit('DEAL_LISTS_GET_REQUEST');

        if (this.props.location) {
            this.handleLocationOverride();
        }
        // Override location and location analysis if visual report is set
        if (this.props.visualReport) {
            this.handleLocationOverrideVisualReport();
            shouldShowInfoBubblePointFeature = [SELFSTORAGE_POINT_TYPE.SS_FACILITY, SELFSTORAGE_POINT_TYPE.SS_CONSTRUCTIONS].includes(
                JSON.parse(this.props.visualReport).definition.pointType,
            );
        }

        this.projectDataSource.setOverrideProjectOptions(this._overrideProjectOptions);

        this.bus.once('FRAME_CONTAINER_MOUNTED', payload => {
            // Override frame if set
            if (this.props.frame) {
                this.handleApplicationFrame(payload);
            }

            this.bus.emit('SHOW_SEARCHED_ITEM_POPUP', {
                forceOpenFacilityPopup: shouldShowInfoBubblePointFeature,
            });
        });
    }

    onDeactivate() {
        this.unbindGluBusEvents();
    }

    /**
     * @param {string} profile
     * @returns {import('../').LocationAnalysisTypeId}
     */
    handleLocationOverrideVisualReport = async () => {
        /** @type {import('../types').VisualReportResponse} */
        const visualReportResponse = JSON.parse(this.props.visualReport);
        let locationAnalysisItem;

        this.closeDefaultTourPopup();

        const { analysisTypeId, selection, ring } = dealAreaFields(visualReportResponse.definition);
        const analysisValues = Array.from(selection);
        const metadata = {
            name: visualReportResponse.title,
            website: visualReportResponse.website,
            has_cc_units: visualReportResponse.has_cc_units,
            rent_sqft: visualReportResponse.rent_sqft,
            rent_sqft_driveup: visualReportResponse.rent_sqft_driveup,
            acres: visualReportResponse.acres,
            owner_name: visualReportResponse.owner_name,
            owner_phone: visualReportResponse.owner_phone,
            full_address: visualReportResponse.definition.address.label,
            facility_id: visualReportResponse.definition.facilityId,
            point: visualReportResponse.definition.point,
            management_type: visualReportResponse.management_type,
            category: visualReportResponse.definition.pointType,
            project_status: visualReportResponse.project_status,
            last_updated_date: visualReportResponse.last_updated_date,
        };

        // Set location analysis item
        locationAnalysisItem = new LocationAnalysisItem({
            type: visualReportResponse.definition.pointType,
            id: visualReportResponse.definition.facilityId,
            value: visualReportResponse.title,
            point: visualReportResponse.definition.point,
            pointType: visualReportResponse.definition.pointType,
            itemOrigin: LocationAnalysisItemOrigin.USER_SAVED_LOCATION,
            analysisTypeId: analysisTypeId,
            selection: selection,
            ring,
            dealId: visualReportResponse.id,
            status: visualReportResponse.status,
            notes: visualReportResponse.notes,
            listId: visualReportResponse.list_id,
            dealListName: visualReportResponse.deal_list_name,
            feature: {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [
                        visualReportResponse.definition.point.lng,
                        visualReportResponse.definition.point.lat,
                    ],
                },
                properties: {
                    ...metadata,
                    pointType: visualReportResponse.definition.pointType,
                    profile: visualReportResponse.definition.profile,
                    selection: selection,
                    address: visualReportResponse.definition.address,
                },
            },
            currentInsightReport: this.props.currentInsightReport,
        });

        this._overrideProjectOptions.locationAnalysisItem = locationAnalysisItem;

        // Save all custom values for analysis
        const analysisType = LocationAnalysisType[analysisTypeId];
        analysisValues
            .filter(value => !analysisType.VALUES_ARRAY.includes(value))
            .forEach(value => {
                this.bus.emit('SAVE_CUSTOM_LOCATION_ANALYSIS_VALUE_REQUEST', {
                    value,
                    analysisTypeUnit: analysisType.UNIT,
                });
            });

        // A custom shape is its own outline, so it needs no contour round-trip.
        const features = locationAnalysisItem.isPolygon
            ? locationAnalysisItem.selectionFeatures.features
            : Object.values(
                  (await this.customMapSelectionDataSource.getContourFeatures(
                      locationAnalysisItem.point,
                      locationAnalysisItem.contourType,
                      locationAnalysisItem.sortedSelectionAsArray,
                      true,
                  ))[locationAnalysisItem.contourType],
              );
        const featureBoundingBox = getFeatureBoundingBox(features);

        // Set initial view override based on the radius/isochrone BBox
        this._overrideProjectOptions.initialView = new InitialView({
            zoom: 14, // falsy zoom... figure out what to do with it
            centerLat: visualReportResponse.definition.point.lat,
            centerLng: visualReportResponse.definition.point.lng,
            boundingBoxNeLat: featureBoundingBox.yMax,
            boundingBoxNeLng: featureBoundingBox.xMax,
            boundingBoxSwLat: featureBoundingBox.yMin,
            boundingBoxSwLng: featureBoundingBox.xMin,
        });
    };

    handleLocationOverride = async () => {
        // Parse location result
        let result;
        switch (this.props.location.searchProvider) {
            case 'here':
                result = parseHereGeocodeResult(this.props.location.searchResult);
                break;
            case 'socex':
                result = parseSocexResult(this.props.location.searchResult);
                break;
            default:
                break;
        }

        let locationAnalysisItem;
        if (this.props.locationAnalysis) {
            this.closeDefaultTourPopup();
            const { analysisTypeId, analysisValues } = this.props.locationAnalysis;
            // Set location analysis item
            locationAnalysisItem = new LocationAnalysisItem({
                id: result.id,
                type: result.type,
                value: result.value,
                point: result.point,
                analysisTypeId,
                selection: new Set(analysisValues),
                itemOrigin: LocationAnalysisItemOrigin.PROJECT_DEFINITION_LOCATION,
                icon: result.icon,
            });
            this._overrideProjectOptions.searchResults = result;
            this._overrideProjectOptions.locationAnalysisItem = locationAnalysisItem;

            // Save all custom values for analysis
            const analysisType = LocationAnalysisType[analysisTypeId];
            analysisValues
                .filter(value => !analysisType.VALUES_ARRAY.includes(value))
                .forEach(value => {
                    this.bus.emit('SAVE_CUSTOM_LOCATION_ANALYSIS_VALUE_REQUEST', {
                        value,
                        analysisTypeUnit: analysisType.UNIT,
                    });
                });

            const locationItemContourData =
                await this.customMapSelectionDataSource.getContourFeatures(
                    locationAnalysisItem.point,
                    locationAnalysisItem.contourType,
                    locationAnalysisItem.sortedSelectionAsArray,
                    true,
                );
            const features = Object.values(
                locationItemContourData[locationAnalysisItem.contourType],
            );
            const featureBoundingBox = getFeatureBoundingBox(features);

            // Set initial view override based on the radius/isochrone BBox
            this._overrideProjectOptions.initialView = new InitialView({
                zoom: 14, // falsy zoom... figure out what to do with it
                centerLat: result.point.lat,
                centerLng: result.point.lng,
                boundingBoxNeLat: featureBoundingBox.yMax,
                boundingBoxNeLng: featureBoundingBox.xMax,
                boundingBoxSwLat: featureBoundingBox.yMin,
                boundingBoxSwLng: featureBoundingBox.xMin,
            });
        } else if (result.boundingBox) {
            this._overrideProjectOptions.initialView = new InitialView({
                zoom: 14,
                centerLat: result.point.lat,
                centerLng: result.point.lng,
                boundingBoxNeLat: result.boundingBox._ne.lat,
                boundingBoxNeLng: result.boundingBox._ne.lng,
                boundingBoxSwLat: result.boundingBox._sw.lat,
                boundingBoxSwLng: result.boundingBox._sw.lng,
            });
        } else {
            this._overrideProjectOptions.initialView = new InitialView({
                zoom: 14,
                centerLat: result.point.lat,
                centerLng: result.point.lng,
            });
        }
    };

    /**
     * @param {object} param0
     * @param {import('../objects/MapInstance').default[]} param0.mapInstances
     */
    handleApplicationFrame = ({ mapInstances }) => {
        // Don't handle anything in case of multiple map instances
        if (mapInstances.length > 1) return;
        // Otherwise change frame
        const mapInstanceId = mapInstances[0].id;

        switch (this.props.frame) {
            case ApplicationFrameType.DATA_FILTER_EDITOR:
                this.closeDefaultTourPopup();
                this.bus.emit('ENTER_DATA_FILTER_MODE', {
                    source: this,
                    mapInstanceId,
                });
                this.bus.once('MAP_LOAD', payload => {
                    if (payload.source.mapInstance.id !== mapInstanceId) return;
                    this.bus.emit('ADD_NEW_FILTER_REQUEST', { mapInstanceId });
                    this.bus.emit('FILTER_SET_ACTIVE', {
                        activeFilterId: undefined,
                    });
                });
                break;
        }
        this.bus.off('FRAME_CONTAINER_MOUNTED', this.handleApplicationFrame);
    };

    closeDefaultTourPopup = () => {
        // We are opening the 'Take a tour' popup for users that are for
        // the first time opening the map app. This popup is designed the way that it can
        // be only show in cases where some components need to be available on the screen
        // (for example the data browser component)
        // With this override options we can get to the situation where we open the 'FILTER PANEL' (for example)
        // for users that are our first time users, and the tour will cause the app to crash, since it can not
        // find the necessary DOM components.
        // Because of that, we will emit the HELP_TOUR_STATUS_UPDATE_REQUEST with isDone set to true, so that
        // the tour popup is not shown.
        // Note: This has to be changed eventually, when our designers find out when is the best time to show
        // this popup when override is active for first time users.
        this.bus.emit('HELP_TOUR_STATUS_UPDATE_REQUEST', {
            isDone: true,
            tourVersion: HelpTourVersions.V1,
        });
    };
}

export default OverrideController;
